<template>
    <div class="container container--facture">  

        <Loader v-if="!ready"></Loader>

        <article class="facture" v-if="ready">

            <header class="facture__heading">
                <h1 class="woocommerce-order-details__title">{{ title }}</h1>
                <div class="facture__heading__date">
                    <p>
                        <strong>Émise le <Date :date="facture.createdAt" /></strong> 
                        <Badge type="danger" v-if="facture.amountRemaining > 0 && facture.isExpired">En retard de paiement</Badge>
                    </p>
                </div>
                <div class="facture__heading__actions">

                    <Button class="woocommerce-button button alt"
                        :disabled="downloading || facture.nbDocuments < 1"
                        :loading="!!downloading"
                        v-if="facture.isReady"
                        @click.native="download()">
                        Télécharger
                        <i class="fa-solid fa-down-to-line"></i>
                   </button>
                   <Badge type="success" v-if="facture.isDigitalSignature">Document signé numériquement</Badge>
                </div>
            </header>         
            
            <div class="facture__details">
                <ul class="woocommerce-order-overview woocommerce-thankyou-order-details order_details">
                    <li class="woocommerce-order-overview__order order" v-if="facture.expiredAt">À régler avant le : <strong><Date :date="facture.expiredAt" /></strong></li>
                    <li class="woocommerce-order-overview__order order">Ref. client : <strong>{{ facture.engagementNum }}</strong></li>
                    <li class="woocommerce-order-overview__order order">Reste à payer: <strong><Price :amount="facture.amountRemaining" :currency="facture.currency" /></strong></li>
                    <li class="woocommerce-order-overview__order order">Montant échu: <strong><Price :amount="facture.amountExpired" :currency="facture.currency" /></strong></li>
                    <li class="woocommerce-order-overview__order order">Montant acquitté: <strong><Price :amount="amountPayed" :currency="facture.currency" /></strong></li>   
                    <li class="woocommerce-order-overview__order order">
                        Commande n°:
                        <strong v-if="facture.commandes.length">
                            <div v-for="commande in facture.commandes"
                                :key="commande.num">
                                <router-link v-if="commande.id" :to="{ name: 'commande', params: { id: commande.id }}">
                                    {{ commande.num }}
                                </router-link>
                                <span v-if="!commande.id">{{ commande.num }}</span>
                            </div>
                        </strong>
                        <span v-if="!facture.commandes.length">Aucune</span>
                    </li>
                    <li class="woocommerce-order-overview__order order" v-if="facture.budgetCode">Code budget : <strong>{{ facture.budgetCode }}</strong></li>
                </ul>
            </div>  

            <div class="woocommerce-order-details">
                <Affaire :data="facture.affaire" />
                <strong v-if="facture.vendeur">Suivie par {{ facture.vendeur.name }}<br /></strong>
            </div>

            <div class="woocommerce-order-details">
                <h3>Adresse de facturation</h3>
                <Address v-if="facture.facturationAddress" :data="facture.facturationAddress"></Address>
                <span v-else>—</span>
            </div> 

            <div class="facture__articles">
                
                <List class="list--table">

                    <template v-slot:checkboxActions>

                        <button class="woocommerce-button button alt"
                            :disabled="processing || !itemSelected"
                            v-on:click="buy()">
                            <span class="fa fa-shopping-cart"></span>
                                Ajouter au panier
                        </button>

                        <Button class="alt"
                            :disabled="!itemSelected"
                            :loading="processingDocuments"
                            @click.native="downloadDocs()">         
                            Télécharger les documentations
                            <i class="fa-solid fa-down-to-line"></i>
                        </Button>

                    </template>

                    <template v-slot:heading>                
                        <div class="cell cell--cb">
                            <TogglerAll />
                        </div>
                        <div class="cell cell--ref">Réf.</div>
                        <div class="cell cell--secondary">UV.</div>
                        <div class="cell cell--secondary">Coef. UV</div>
                        <div class="cell cell--main">Libellé</div>
                        <div class="cell cell--secondary">Code adresse</div>
                        <div v-if="currentClient.showDiscount && table.columns.priceRawHTT" class="cell cell--price">Tarif brut</div>
                        <div v-if="currentClient.showDiscount && table.columns.remiseClient" class="cell cell--secondary">% Remise</div>
                        <div class="cell cell--secondary">PU</div>
                        <div class="cell">Qté</div>
                        <div class="cell cell--price">Total HTT</div>
                        <div class="cell cell--secondary">TGAP</div>
                        <div class="cell cell--secondary">TVA</div>       
                    </template>

                    <template v-slot:footer>                
                        <div class="list__footer__row">
                            <div class="cell">Frais de livraison HT</div>
                            <div class="cell"><Price :amount="facture.deliveryCost" :currency="facture.currency" /></div>
                            <div></div>
                        </div>
                        <div v-if="facture.ftc && facture.ftc > 0" class="list__footer__row">
                            <div class="cell">                            
                                {{$setting('FTC_LABEL')}} HT
                            </div>

                            <div class="cell"><Price :amount="facture.ftc" :currency="facture.currency" /></div>
                            <div></div>
                        </div>                          
                        <div class="list__footer__row">
                            <div class="cell">Total HT</div>
                            <div class="cell"><Price :amount="facture.amountHT" :currency="facture.currency" /></div>
                            <div></div>
                        </div>
                        <div class="list__footer__row">
                            <div class="cell">Total TTC</div>
                            <div class="cell"><Price :amount="facture.amountTTC" :currency="facture.currency" /></div>
                        </div>
                    </template>

                    <ul class="list__content">

                        <li class="list__content__item"
                            v-for="ligne in facture.lignes"
                            :key="ligne.id">

                            <ArticleItem 
                                :data="ligne">

                                <div class="cell cell--cb">
                                    <input type="checkbox"
                                        :disabled="!ligne.articleSlug"
                                        :value="ligne"
                                        v-model="selectedItems"
                                        @click.capture="$event.stopPropagation()" />
                                </div>
                                <div class="cell cell--ref">{{ ligne.articleReference }}</div>
                                <div class="cell cell--secondary">{{ ligne.uvCodeLibelle }}</div>
                                <div class="cell cell--secondary">x{{ ligne.uvCoef }}</div>
                                <div class="cell cell--main">{{ ligne.libelle }}</div>
                                <div class="cell cell--secondary">{{ ligne.addressLivraisonCode }}</div>
                                <div v-if="currentClient.showDiscount && table.columns.priceRawHTT" class="cell cell--price"><Price :amount="priceRawHTT(ligne)" :currency="facture.currency" /></div>
                                <div v-if="currentClient.showDiscount && table.columns.remiseClient" class="cell cell--secondary">{{ remiseClientPct(ligne) }}</div>
                                <div class="cell cell--secondary"><Price :amount="ligne.priceHTT" :currency="facture.currency" /></div>
                                <div class="cell cell--qty">{{ ligne.livreeQty }}</div>
                                <div class="cell cell--price"><Price :amount="ligne.amountHTT" :currency="facture.currency" /></div>
                                <div class="cell cell--secondary"><Price :amount="ligne.tgap" :currency="facture.currency" /></div>
                                <div class="cell cell--secondary"><Price :amount="ligne.tva" /></div>
                                
                            </ArticleItem>

                        </li>
                        
                    </ul>
                    
                </List>

            </div>

        </article>

    </div>
</template>

<script>

import { mapState } from 'vuex'

import api from '@/api/proxyWeb/factures'
import { actions } from '@/store/factures'

import cart from '@/containers/cart.vue'
import title from "@/containers/title"

import Address from '@/components/address/Address.vue'
import Affaire from '@/components/affaire/Affaire.vue'
import Button from '@/components/button/Button.vue'
import ArticleItem from '@/components/articleItem/ArticleItem.vue'
import Badge from '@/components/badge/Badge.vue'
import Date from '@/components/date/Date.vue'
import List, { TogglerAll }  from '@/components/list/List.vue'
import Loader from '@/components/loader/Loader.vue'
import Price from '@/components/price/Price.vue'

import selectAll from '@/containers/selectAll.vue'


export default {

    name: 'Facture',

    data() {
        return {
            downloading: false,
            processingDocuments: false
        }
    },

    computed: mapState({

        amountPayed: state => {
            return state.factures.current.amountTTC - state.factures.current.amountRemaining
        },

        facture: state => state.factures.current,

        ready: state => ( state.factures.current !== null ),

        title: state => {
            return ( ( state.factures.current.isAvoir ) ? 'Avoir' : 'Facture' ) + ' ' + state.factures.current.num
        },

        currentClient: state => state.clients.current

    }),

    methods: {

        buy() {

            let articles = this.selectedItems.map(factureLigne => {
                    return {
                        'id': factureLigne.articleId,
                        'quantity': factureLigne.livreeQty,
                        'variationId': factureLigne.articleVariationId
                    }
                })

            this.addToCart(articles);
        },        

        download() {

            if ( !! this.downloading || this.facture.nbDocuments < 1 ) return

            this.downloading = true;
            api.getFile(this.facture.id)
                .then(() => this.$notify.success('Téléchargement en cours...') )
                .catch(() => this.$error( 'Impossible de télécharger les documents' ))
                .finally(() => { this.downloading = false } ) 

        },

        downloadDocs() {
            this.processingDocuments = true
            api.getDocuments(this.facture.id, this.selectedItems.map( factureLigne => factureLigne.num ))
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingDocuments = false } )
        },

        priceRawHTT: function(ligne) {
            return ( ligne.priceHTT >= ligne.priceRawHTT ) ? null : ligne.priceRawHTT
        },

        remiseClientPct: function(ligne) {
            return ( ligne.remiseClient == null || ligne.priceHTT >= ligne.priceRawHTT ) ? null : ligne.remiseClient * 100 + '%'
        }
    },
  
    created () {
        
        api.get(this.$route.params.id + this.$route.meta.tablePath)
            .then( (content) => 
            {
                this.table = content
            })
            .catch(error => {
                this.$errorPage(error.reason)
            })  


        this.$store.dispatch(actions.GET, this.$route.params.id)
            .then( () => this.setTitle( this.title ) )
            .catch(error => {
                this.$errorPage(error.reason)
            })
    },

    watch:{

        facture: function() {            

            this.selectableItems = []

            if ( !this.facture ) {
                return
            }
            
            this.facture.lignes.forEach(ligne => {
                if ( !ligne.articleSlug ) return
                this.selectableItems.push(ligne)
            })
        }
    },

    components: {
        Address,
        Affaire,
        ArticleItem,
        Badge,
        Button,
        Date,
        List,
        Loader,
        Price,
        TogglerAll
    },

    mixins: [ selectAll, title, cart ]
}
</script>

<style>

.facture {
    position: relative;
}

.facture__heading {
    margin-bottom: 2rem;
    padding-top: 4rem; 
}

.facture__heading__actions {
    position: absolute;
    right: 0;
    top: 0;
    text-align: right;
}

.facture__heading__actions > *:not(:first-child) {
    margin-left: 1rem !important;
}

.facture__footer {
}

.facture__footer__actions {
    text-align: right;
}

.cell--secondary {
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>
